import { useMemo } from 'react';
import {
  Badge,
  Flex,
  Table,
  Icon,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { useGlobalFilter, usePagination, useSortBy, useTable } from 'react-table';
import { MdCheckCircle, MdOutlineError, MdPendingActions } from 'react-icons/md';

import Card from 'components/card/Card';
import { Applications, ApplicationStatus } from '../../../../api/applications';
import { prettifyDate } from '../../../../functions/dateUtils';

export default function ColumnsTable({ columnsData, tableData }) {
  const columns = useMemo(() => {
    if (!tableData?.length) return columnsData;

    const sampleRow = tableData[0];

    // Filter out columns that don't exist in the data
    return columnsData.filter(
      (column) => column.accessor in sampleRow || tableData.some((row) => column.accessor in row),
    );
  }, [columnsData, tableData]);

  const data = useMemo(() => tableData, [tableData]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 6 }, // set initial page size here
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
  );

  const { getTableProps, getTableBodyProps, headerGroups, page, prepareRow } = tableInstance;

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHoverBg = useColorModeValue('blue.50', 'whiteAlpha.100');
  const headerBg = useColorModeValue('white', 'whiteAlpha.100');

  return (
    <Card
      direction="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
      boxShadow="0px 4px 20px rgba(0, 0, 0, 0.05)"
      borderRadius="12px"
    >
      <Flex px="16px" justify="space-between" mb="12px" pt="8px" align="center">
        <Text
          color={textColor}
          fontSize={{ base: '16px', md: '18px' }}
          fontWeight="600"
          lineHeight="1.2"
          letterSpacing="0">
          העברות אחרונות
        </Text>
      </Flex>
      <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index} bg={headerBg}>
              {headerGroup.headers.map((column, index) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  pe="10px"
                  key={index}
                  borderColor={borderColor}
                  py="14px"
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    fontSize="12px"
                    color="gray.500"
                    fontWeight="600"
                    letterSpacing="0"
                    textTransform="none"
                  >
                    {column.render('Header')}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {page.map((row, index) => {
            prepareRow(row);
            return (
              <Tr
                {...row.getRowProps()}
                key={index}
                _hover={{ bg: rowHoverBg, transition: 'background 0.2s ease' }}
                cursor="default"
              >
                {row.cells.map((cell, index) => {
                  let data = '';
                  if (cell.column.id === 'fullName') {
                    data = (
                      <Text color={textColor} fontSize="14px" fontWeight="600" lineHeight="1.4" letterSpacing="0">
                        {cell.value}
                      </Text>
                    );
                  } else if (cell.column.id === 'status') {
                    // Determine badge styling based on status
                    const isSuccess =
                      cell.value === ApplicationStatus.PendingTransfer ||
                      cell.value === ApplicationStatus.Transferred ||
                      cell.value === ApplicationStatus.PendingApproval;
                    const isPending =
                      cell.value === ApplicationStatus.Canceled ||
                      cell.value === ApplicationStatus.Declined ||
                      cell.value === 15 ||
                      cell.value === 10;
                    const isError = cell.value === 'שגיאה';

                    const badgeColorScheme = isSuccess ? 'green' : isPending ? 'orange' : isError ? 'red' : 'gray';
                    const iconComponent = isSuccess ? MdCheckCircle : isPending ? MdPendingActions : isError ? MdOutlineError : null;

                    data = (
                      <Badge
                        colorScheme={badgeColorScheme}
                        px="12px"
                        py="6px"
                        borderRadius="8px"
                        fontSize="11px"
                        fontWeight="600"
                        letterSpacing="0"
                        display="flex"
                        alignItems="center"
                        gap="6px"
                        w="fit-content"
                      >
                        {iconComponent && (
                          <Icon
                            w="14px"
                            h="14px"
                            as={iconComponent}
                          />
                        )}
                        {Applications.statusText(cell.value)}
                      </Badge>
                    );
                  } else if (cell.column.id === 'amount') {
                    data = (
                      <Flex align="center">
                        <Text
                          color={textColor}
                          fontSize="14px"
                          fontWeight="600"
                          lineHeight="1.4"
                          letterSpacing="0"
                          fontFeatureSettings='"tnum" on, "lnum" on'>
                          ₪{Number(cell.value).toLocaleString('en-US')}
                        </Text>
                      </Flex>
                    );
                  } else if (cell.column.id === 'createdAt') {
                    data = (
                      <Text
                        color={textColor}
                        fontSize="14px"
                        fontWeight="600"
                        lineHeight="1.4"
                        letterSpacing="0"
                        fontFeatureSettings='"tnum" on, "lnum" on'>
                        {prettifyDate(cell.value)}
                      </Text>
                    );
                  } else if (cell.column.id === 'companyName') {
                    data = (
                      <Text color={textColor} fontSize="14px" fontWeight="500" lineHeight="1.4" letterSpacing="0">
                        {cell.value}
                      </Text>
                    );
                  }
                  return (
                    <Td
                      {...cell.getCellProps()}
                      key={index}
                      fontSize="14px"
                      py="10px"
                      px="16px"
                      minW={{ sm: '120px', md: '150px', lg: 'auto' }}
                      borderColor="transparent"
                    >
                      {data}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}
